import $ from '@nuskin/nuskinjquery';
import {UserService} from '@nuskin/ns-account';
import {retrieveTogglesBasedOnEnvironment} from '@nuskin/ns-feature-flags';
import { getCachedConfigField, getConfiguration } from '@nuskin/configuration-sdk';
import {
    events,
    StringService,
    RunConfigService,
    storage,
    NsNotQualifiedException,
    UrlService
} from '@nuskin/ns-util';
import {CartService} from '@nuskin/ns-shop';
import {ProductDataService} from '@nuskin/ns-product'
import { default as NewQualificationService } from '@nuskin/exclusive-offer-sdk';

import axios from 'axios';

const QUALIFICATION_URL = "/qualification/v1";
const DFLT_NOT_LOGGED_IN = "You must be logged in to purchase exclusive products.";

let _cache = null;
let manage = false;
let _activeEvents = [];
let getPromise = {promise: null};
let managePromise = {promise: null};

let variantMappings = {}

let flagsPromise = null
let featureFlags = false
const checkFlag = async (flag) => {
    if (!featureFlags && !flagsPromise) {
        flagsPromise = retrieveTogglesBasedOnEnvironment().then(flagsFromServer => featureFlags = flagsFromServer)
    }

    if (!featureFlags) {
        await flagsPromise
    }

    return featureFlags.has(flag)
}

/*
-----------------------------
Private Methods
-----------------------------
 */

/**
 * Creates a headers object for the AWS service call.
 *
 * @param jwt
 * @returns {{Accept: string, "Content-Type": string, client_id: *, client_secret: *}}
 * @private
 */
const _getHeaders = (jwt) => {
    let headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'client_id': RunConfigService.getRunConfig().clientId
    };

    if (jwt) {
        headers.Authorization = `JWT ${jwt}`;
    }
    return headers;
};

const _mapSkusToBase = async (skuList, exclusiveProductsOnly) => {

    // load product data
    await loadVariantMappings(skuList)

    return skuList
        .map(sku => '' + sku)
        .filter(sku => variantMappings[sku].isExclusive || !exclusiveProductsOnly)
        .reduce((result, sku) => {
            result[sku] = variantMappings[sku].sku
            return result
        }, {})
}

const _loadAddons = async (qualifications) => {
    const skuList = Object.keys(qualifications)
        .filter(sku => !qualifications[sku].hasOwnProperty('addOns'))

    let products = {}
    // Don't try to load empty lists
    if (skuList.length > 0) {
        products = (await ProductDataService.getProductData(skuList)).products
            .reduce((result, product) => {
                result[product.sku] = product
                return result
            }, {})
        await Promise.all(
            skuList.map(sku =>
                // eslint-disable-next-line no-async-promise-executor
                new Promise(async (resolve) => {
                    try {
                        const addOns = products[sku] ? products[sku].addOns : null;

                        if (addOns && Array.isArray(addOns)) {
                            qualifications[sku].addOns = addOns.map(element => element.sku ? element.sku : element + '')
                        }
                    } catch (e) {
                        qualifications[sku].addOns = {
                            errorMessage: 'Error loading sku'
                        };
                    }
                    resolve();
                })
            ))
    }

    return qualifications;
}

/**
 * Initializes qualifications from the server. Returns a list of qualification
 *  objects with adjusted quantity based off whats in the cart and other info
 *  helpful to the UI
 *
 * @param qualifications - qualifications from the server
 * @private
 */
const _initQualifications = (qualifications) => {
    _cache = qualifications;

    Object.values(_cache)
        .forEach(item => {
            item.originalQuantity = item.quantity;
        });

    _setActiveFlag();
    _setQualificationsBanner();
    // return a copy of the qualification
    return _copyQualifications();
};

/*
const _getQualificationFromCache = (sku) => {
    return _cache ? _cache[sku] : null;
};
*/

const _setQualificationsInCache = (qualifications) => {
    if (_cache) {
        Object.values(qualifications).forEach((qualification) => {
            _cache[qualification.sku] = qualification;
            qualification.originalQuantity = qualification.quantity;
            _setActiveFlag();
            return Object.assign({}, qualification);
        });
    }
    _setQualificationsBanner();
    return _copyQualifications();
};

const _copyQualifications = () => {
    let retVal = null;

    if (_cache) {
        retVal = Object.values(_cache)
            .reduce((map, item) => {
                map[item.sku] = Object.assign({}, item);
                return map;
            }, {});
    }

    return retVal;
};

const _setActiveFlag = () => {
    let statusChanged = false;

    if (_cache) {
        Object.values(_cache)
            .forEach((item) => {
                let currentStatus = item.isConsumable;

                item.isConsumable = !item.eventName || _activeEvents.includes(item.eventName) ? true : false;
                statusChanged = statusChanged || currentStatus !== item.isConsumable;
            });
    }

    return statusChanged;
};

/**
 * Hide/Show Qualifications banner
 * @param value true/false
 */
const _setQualificationsBanner = () => {
    let showBanner = false;
    if (_cache) {
        showBanner = Object.values(_cache).some(q => {
            return q.quantity > 0 || q.sharedList.length > 0;
        });
    }
    storage.setItem(storage.metadata.SHOW_QUALIFICATIONS_MESSAGE, showBanner);
};

const _clearQualificationsBanner = () => {
    storage.removeItem(storage.metadata.SHOW_QUALIFICATIONS_MESSAGE);
};

const _clearQualifications = () => {
    _cache = null;
    _clearQualificationsBanner();
};

/**
 * Filters out any qualifications for an exclusive context
 *
 * @param qualification
 * @returns filteredQualification
 * @private
 */
const _filterExclusiveContext = (qualification) => {
    let microsite = getCachedConfigField('microsite');
    return Object.keys(qualification)
        .filter(key => {
            return (qualification[key].exclusiveContext === microsite || !qualification[key].exclusiveContext && !microsite);
        })
        .reduce((obj, k) => {
            obj[k] = qualification[k];
            return obj;
        },{});
};

const _getQueryParams = (params) => {
    const seParams = storage.getItem({key: 'seParams', type: 'Required', storageType: 'localStorage' }),
        ignoreActiveDates = seParams ? seParams.showQualifications === true : false;
    return Object.assign({
        ignoreActive: ignoreActiveDates,
        locale: `${RunConfigService.getRunConfig().language}_${RunConfigService.getRunConfig().country}`
    }, params);
};

/**
 * subscribes to logout event and clearsQualifications and subscribes to active events to update qualification status
 * @private
 */
const _setListeners = () => {
    events.subscribe(events.authentication.LOGOUT, function () {
        _clearQualifications();
    });

    events.getValue(events.salesevent.ACTIVE_EVENTS, (activeEvents) => {
        _activeEvents = activeEvents || [];
        if (_setActiveFlag()) {
            events.setValue(events.shop.QUALIFICATION_STATUS_CHANGED);
        }
    }, true);
};

/*
------------------------------
Public Methods
------------------------------
 */

/**
 * @typedef Qualification
 * @property {}
 *
 * Gets the qualifications for the logged in user.  This call currently assumes manage mode.
 * @param {boolean} manage - manage if true returns all sharable information in addition to the qualification.  (More expensive call)
 * @returns {Promise<Object>}
 */
const getQualifications = async (mng = true) => {
    let retVal = null;
    let p = mng ? managePromise : getPromise;
    if (UserService.isLoggedIn()) {
        if (!manage && mng) {
            manage = mng;
        } else {
            retVal = _copyQualifications();
        }
        if (!retVal) {
            try {
                if (!p.promise) {
                    p.promise = axios({
                        method: 'GET',
                        url: `${UrlService.getAwsUrl()}${QUALIFICATION_URL}/customer/${UserService.getUser().id}`,
                        headers: _getHeaders(UserService.getUser().eid),
                        params: _getQueryParams({manage: mng})
                    });
                }
                let response = await p.promise;
                retVal = _initQualifications(await _loadAddons(_filterExclusiveContext(response.data.qualification)));
                p.promise = null;
            } catch (err) {
                // call failed
                console.log(err);
                throw err;
            }
        }
    } else {
        let str = DFLT_NOT_LOGGED_IN;
        try {
            str = await StringService.getString('signin', DFLT_NOT_LOGGED_IN);
        } catch (e) {
            console.error(e);
        }
        throw new NsNotQualifiedException(str);
    }
    return retVal;
};

/**
 * Marks the users qualifications as viewed
 *
 * @param skus - array of SKUs
 * @param viewShared - true if manage shared is viewed (vs qualifications viewed)
 * @return {Promise<{qualifications: null, messages: (*|Array)}>}
 */
const qualificationsViewed = async (skus = null, viewShared = false) => {
    let result;
    if (UserService.isLoggedIn()) {
        const userId = UserService.getUser().id;
        const body = {
            customerId: userId,
            allViewed: !(skus && Array.isArray(skus)),
            skus: skus
        };
        try {
            let response = await axios({
                method: 'POST',
                url: `${UrlService.getAwsUrl()}${QUALIFICATION_URL}/viewed`,
                headers: _getHeaders(UserService.getUser().eid),
                data: body,
                params: _getQueryParams({manage: true, viewShared: viewShared})
            });
            result = {
                qualifications: _setQualificationsInCache(await _loadAddons(_filterExclusiveContext(response.data.qualification))),
                messages: response.data.messages || []
            }
        } catch (err) {
            console.log(err);
            throw err;
        }
    } else {
        let str = DFLT_NOT_LOGGED_IN;
        try {
            str = await StringService.getString('signin', DFLT_NOT_LOGGED_IN);
        } catch (e) {
            console.error(e);
        }
        throw Error(str);
    }
    return result;
}

/**
 * Marks the users qualifications as claimed
 *
 * @Typedef Claim
 * @property {number} quantity
 * @property {string} sku
 * @property {string} customerId - the ID of the distributor they received the qualification from
 *
 * @param {Array.<Claim>} claims
 * @returns {Promise<Object>}
 */
const claimQualifications = async (claims) => {
    let result;
    if (UserService.isLoggedIn()) {
        let userId = UserService.getUser().id;
        let body = {
            customerId: userId,
            claims: claims
        };
        try {
            let response = await axios({
                method: 'POST',
                url: `${UrlService.getAwsUrl()}${QUALIFICATION_URL}/claim`,
                headers: _getHeaders(UserService.getUser().eid),
                data: body,
                params: _getQueryParams({manage: true})
            });
            result = {
                qualifications: _setQualificationsInCache(await _loadAddons(_filterExclusiveContext(response.data.qualification))),
                messages: response.data.messages || []
            }
        } catch (err) {
            console.log(err);
            throw err;
        }
    } else {
        let str = DFLT_NOT_LOGGED_IN;
        try {
            str = await StringService.getString('signin', DFLT_NOT_LOGGED_IN);
        } catch (e) {
            console.error(e);
        }
        throw Error(str);
    }
    return result;
}

/**
 * Takes back qualifications from users they were shared to
 *
 * @Typedef ReclaimFrom
 * @property {number} quantity
 * @property {string} sku
 * @property {string} customerId - the ID of the distributor they received the qualification from
 *
 * @param {Array.<ReclaimFrom>} from
 * @returns {Promise<Object>}
 */
const reclaimQualifications = async (from) => {
    let result;
    if (UserService.isLoggedIn()) {
        let userId = UserService.getUser().id;
        let body = {
            customerId: userId,
            from: from
        };
        try {
            let response = await axios({
                method: 'POST',
                url: `${UrlService.getAwsUrl()}${QUALIFICATION_URL}/reclaim`,
                headers: _getHeaders(UserService.getUser().eid),
                data: body,
                params: _getQueryParams({manage: true})
            });
            result = {
                qualifications: _setQualificationsInCache(await _loadAddons(_filterExclusiveContext(response.data.qualification))),
                messages: response.data.messages || []
            }
        } catch (err) {
            console.log(err);
            throw err;
        }
    } else {
        let str = DFLT_NOT_LOGGED_IN;
        try {
            str = await StringService.getString('signin', DFLT_NOT_LOGGED_IN);
        } catch (e) {
            console.error(e);
        }
        throw Error(str);
    }
    return result;
}

/**
 * @typedef QualificationTransfer
 * @property {string} customerId
 * @property {string} sku
 * @property {number} quantity
 *
 * @param {Array.<QualificationTransfer>}
 * @returns {Promise<void>}
 */
const transferQualifications = async (transfers) => {
    let result;
    if (UserService.isLoggedIn()) {
        let userId = UserService.getUser().id;
        let body = {
            action: "transfer",
            customerId: userId,
            to: transfers
        };
        try {
            let response = await axios({
                method: 'POST',
                url: `${UrlService.getAwsUrl()}${QUALIFICATION_URL}/transfer`,
                headers: _getHeaders(UserService.getUser().eid),
                data: body,
                params: _getQueryParams({manage: true})
            });
            result = {
                qualifications: _setQualificationsInCache(await _loadAddons(_filterExclusiveContext(response.data.qualification))),
                messages: response.data.messages || []
            }
        } catch (err) {
            console.log(err);
            throw err;
        }
    } else {
        let str = DFLT_NOT_LOGGED_IN;
        try {
            str = await StringService.getString('logInToUseQualification', DFLT_NOT_LOGGED_IN);
        } catch (e) {
            console.error(e);
        }
        throw Error(str);
    }
    return result;
};

const returnPartialQualifications  = async (sku, returnArray) => {
    let currentQualifications = await getQualifications();
    let transfers = [];

    //We need to iterate through the shareback list and return the number that the user doesn't want.
    returnArray.forEach((returnObj) => {
        let returningTotal = 0;
        //We need to make sure that we are not trying to transfer back more than the user gave us:
        let possibleShareBackQuantity = 0;
        currentQualifications[sku].shareBackList.forEach((shareBack) => {
            if(shareBack.customerId === returnObj.customerId){
                possibleShareBackQuantity = shareBack.quantity;
            }
        })

        //If the user is trying to share back more than was originally given, only return what was originally given.
        if(possibleShareBackQuantity < returnObj.quantity){
            returningTotal = possibleShareBackQuantity;
        } else {
            returningTotal = returnObj.quantity;
        }

        transfers.push({customerId: returnObj.customerId, sku: sku, quantity: returningTotal})
    })

    return await transferQualifications(transfers);
}

/**
 * Returns all the qualifications back to everyone in the sharebacklists
 *
 * @returns {Promise<void>}
 */
const returnQualifications = async (sku) => {
    let qualifications = await getQualifications();
    let transfers = [];
    // Create transfers from qualification sharebacklist
    qualifications[sku].shareBackList
        .forEach((shareBack) => transfers.push({
            customerId: shareBack.customerId,
            sku: sku,
            quantity: shareBack.quantity
        }));

    return await transferQualifications(transfers);
};

/**
 * Gets a single user qualification
 *
 * @param sku
 * @returns {Promise<*>}
 */
const getQualification = async (sku) => {

    const featureEnabled = await checkFlag('cx15-4373')
    if (featureEnabled) {
        sku = (await _mapSkusToBase([sku]))[sku]
    }

    let qualifications =  await getQualifications();
    return qualifications[sku];
};

/**
 * Converts variants to base skus
 *
 */

const convertCartDataToBaseSkus = async (cartList, exclusiveProductsOnly) => {

    const mappings = await _mapSkusToBase(cartList.map(entry => entry.sku), exclusiveProductsOnly)

    const result = cartList
        .filter(item => mappings[item.sku])
        .reduce((result, item) => {
            const baseSku = mappings[item.sku]
            if (!result[baseSku]) {
                result[baseSku] = {
                    sku: baseSku,
                    qty: 0
                }
            }
            result[baseSku].qty += (item.qty ? item.qty : 0)
            return result
        }, {})
    return result
}

/**
 * Checks quantity and salesEvent status of a qualification
 *
 * @param sku
 * @param qty
 * @returns {Promise<*>}
 */
const isQualified = async (sku, qty) => {
    const featureEnabled = await checkFlag('cx15-4373')
    if (featureEnabled) {
        const cartItem = CartService.getItemData().find(item => item.sku == sku)
        let quantityChange = qty
        if (cartItem) {
            quantityChange = qty - cartItem.qty
        }

        sku = (await _mapSkusToBase([sku]))[sku]

        if (!sku) {
            return true// the product isn't exclusive
        }

        const newCartContentsBaseSkus = await convertCartDataToBaseSkus(
            [...CartService.getItemData(), {sku: sku, qty: quantityChange}],
            true)
        const qualification = await getQualification(sku);

        if (qualification && qualification.quantity < newCartContentsBaseSkus[sku].qty) {
            await CartService.checkItemAvailability();
            return false;
        }

        return qualification && qualification.quantity >= newCartContentsBaseSkus[sku].qty && qualification.isConsumable
    } else {
        let retVal = false,
            qualification = await getQualification(sku);

        if (qualification && qualification.quantity >= qty) {
            retVal = qualification.isConsumable;
        }

        return retVal;
    }
};

const getQualifiedQty = async (sku) => {
    let retVal = 0,
        qualification = await getQualification(sku);

    if (qualification && qualification.isConsumable) {
        retVal = qualification.quantity;
    }

    return retVal;
};

/**
 * Allows you to pass a list of product sku's to load variant data for
 * @param {*} skuList
 */
const loadVariantMappings = async (skuList) => {
    const filteredSkuList = skuList
        .map(sku => '' + sku)
        .filter(sku => !variantMappings[sku])

    if (filteredSkuList.length == 0) {
        return []
    }

    const {products} = await ProductDataService.getProductData(filteredSkuList)
    products.forEach(product => {
        Object.keys(product.variants)
            .forEach(sku => {
                variantMappings[sku] = {
                    isExclusive: product.variants[sku].isExclusive,
                    sku: product.sku || sku
                }
            })

        variantMappings[product.sku] = {
            isExclusive: product.isExclusive,
            sku: product.sku
        }
    })
    return products
}

/**
 * Return whether to show qualification banner
 */
const getQualificationBanner = () => {
    return storage.getItem(storage.metadata.SHOW_QUALIFICATIONS_MESSAGE);
};

/**
 * Set Early Access Qualification modal
 * @param value true/false
 */
const setEarlyAccessQualificationModal = (value) => {
    value ? storage.setItem(storage.metadata.VIEWED_QUALIFICATION_MODAL, value) : storage.removeItem(storage.metadata.VIEWED_QUALIFICATION_MODAL);
};

/**
 * Get Early Access Qualification modal
 */
const getEarlyAccessQualificationModal = () => {
    return storage.getItem(storage.metadata.VIEWED_QUALIFICATION_MODAL) ? true : false;
};

$(document).ready(_setListeners);

const OldQualificationService = {
    _clearQualifications,   // exposed for unit testing
    _setListeners,          // Only exposed for unit testing purposes this is called above in the jquery ready function
    claimQualifications,
    convertCartDataToBaseSkus,
    getEarlyAccessQualificationModal,
    getQualification,
    getQualificationBanner,
    getQualifications,
    getQualifiedQty,
    isQualified,
    loadVariantMappings,
    qualificationsViewed,
    reclaimQualifications,
    returnPartialQualifications,
    returnQualifications,
    setEarlyAccessQualificationModal,
    transferQualifications
};

/*
    The code below will globally switch this service over to the new exclusive offer service in markets where that is appropriate

    For each method in the service, it will switch between old and new service based on the equinox configuration and feature flag.
    If the method isn't in the new service or if there is an error fetching, it will default to the old one
*/
const getQualificationService = async () => {
    try {
        const [
            flagSet,
            configEnabled
        ] = await Promise.all([
            checkFlag('cx15_use_new_exclusive_offer_api'),
            getConfiguration(['Equinox_Markets']).then(configuration => configuration.Equinox_Markets.active)
        ])
        return flagSet || configEnabled ? NewQualificationService : OldQualificationService;
    } catch (e) {
        console.error('error retrieving configuration. Defaulting to Legacy exclusive offer service', e)
        return OldQualificationService
    }
}

const invokeQualificationServiceMethod = async (params, functionName) => {
    const qualificationService = await getQualificationService()
    return qualificationService[functionName](...params)
}

const QualificationService = Object.keys(OldQualificationService)
    .reduce((QualificationService, functionName) => {
        if (NewQualificationService[functionName]) {
            QualificationService[functionName] = (...params) => invokeQualificationServiceMethod(params, functionName)
        } else {
            QualificationService[functionName] = OldQualificationService[functionName]
        }

        return QualificationService
    }, {})

export default QualificationService;
